import React from 'react';
import { Redirect } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';
import { useAppConfig } from '@/modules/config';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import { TopicCreateOrUpdatePage } from '@/modules/topic_garden';
import type { TopicTypeId } from './types';
import { enabledTopicTypes } from './topicTypeRegistry';

/**
 * Type-specific create route (`/topics/new-<type>`, e.g. `/topics/new-deal`). Reached
 * from the `/topics/new` chooser once a type is picked; the type lives in the path so
 * the form is a real routed page with a natural back button (to `/topics`).
 *
 * The route captures the whole `new-<type>` segment as `createType`; strip the `new-`
 * prefix to get the type id.
 *
 * We render the create page (an `IonPage`) up front and only redirect back to
 * `/topics/new` once config has POSITIVELY loaded and the type isn't available (garden
 * off, or an unknown / currently-disabled type like `news` when its flag is off). We
 * must not return a `<Redirect>` before `isLoaded`: on a hard reload landing directly
 * here, `features` is momentarily `{}`, and establishing a `<Redirect>` (rather than an
 * `IonPage`) as the outlet's view for this location leaves the real page unable to
 * mount once config arrives — the same `IonRouterOutlet` gotcha the `/register` route
 * guards against.
 */
const TopicCreateForType: React.FC<
  RouteComponentProps<{ createType: string }>
> = ({ match }) => {
  const { features, isLoaded } = useAppConfig();
  const typeId = match.params.createType.replace(/^new-/, '') as TopicTypeId;

  if (isLoaded) {
    const available =
      topicGardenEnabled(features) &&
      enabledTopicTypes(features).some(t => t.id === typeId);
    if (!available) {
      return <Redirect to="/topics/new" />;
    }
  }

  return <TopicCreateOrUpdatePage initialMode="create" createType={typeId} />;
};

export default TopicCreateForType;
